import React, { useState, useEffect } from 'react';
import { FaPercent, FaCoins, FaTimes, FaCheck, FaTrash, FaCalculator } from 'react-icons/fa';
import './BillAdjustmentModals.css';

/**
 * BillDiscountModal Component
 * Handles adding or changing the overall bill discount on a purchase order.
 */
export const BillDiscountModal = ({
  isOpen,
  onClose,
  onApply,
  currentDiscount = 0,
  taxableSubTotal = 0,
  grossTotal = 0
}) => {
  const [discountVal, setDiscountVal] = useState(currentDiscount || 0);

  useEffect(() => {
    if (isOpen) {
      setDiscountVal(currentDiscount || 0);
    }
  }, [isOpen, currentDiscount]);

  if (!isOpen) return null;

  const baseAmount = grossTotal > 0 ? grossTotal : taxableSubTotal;

  const handleApplyPct = (pct) => {
    const val = Number(((baseAmount * pct) / 100).toFixed(2));
    setDiscountVal(val);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const val = Math.max(0, Number(discountVal) || 0);
    onApply(val);
    onClose();
  };

  const handleRemove = () => {
    onApply(0);
    onClose();
  };

  const previewTotal = Math.max(0, baseAmount - (Number(discountVal) || 0));

  return (
    <div className="vt-adj-backdrop" onClick={onClose}>
      <div className="vt-adj-card" onClick={(e) => e.stopPropagation()}>
        <div className="vt-adj-header">
          <div className="vt-adj-title-box">
            <div className="vt-adj-icon discount">
              <FaPercent size={15} />
            </div>
            <div>
              <h3 className="vt-adj-title">{currentDiscount > 0 ? 'Change Bill Discount' : 'Add Bill Discount'}</h3>
              <p className="vt-adj-sub">Treated as overall bill discount across this purchase order.</p>
            </div>
          </div>
          <button type="button" className="vt-adj-close" onClick={onClose}>
            <FaTimes size={13} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="vt-adj-body">
          <div className="vt-adj-info-row">
            <span>Bill Gross Total:</span>
            <strong>₹ {baseAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
          </div>

          <div className="vt-adj-field">
            <label>Bill Discount Amount (₹):</label>
            <input
              type="number"
              min="0"
              max={baseAmount}
              step="0.01"
              className="vt-adj-input"
              value={discountVal === 0 ? '' : discountVal}
              placeholder="Enter discount in ₹..."
              autoFocus
              onChange={(e) => setDiscountVal(e.target.value)}
            />
          </div>

          {/* Quick Percentage Chips */}
          <div className="vt-adj-pct-row">
            <span className="pct-label">Quick %:</span>
            {[2, 5, 10, 15, 20].map((pct) => (
              <button
                key={pct}
                type="button"
                className="vt-adj-pct-chip"
                onClick={() => handleApplyPct(pct)}
              >
                {pct}%
              </button>
            ))}
          </div>

          {/* Preview Box */}
          <div className="vt-adj-preview-banner">
            <div className="preview-line">
              <span>Bill Discount:</span>
              <strong className="discount-figure">- ₹ {(Number(discountVal) || 0).toFixed(2)}</strong>
            </div>
            <div className="preview-line final">
              <span>Payable After Discount:</span>
              <strong>₹ {previewTotal.toFixed(2)}</strong>
            </div>
          </div>

          <div className="vt-adj-footer">
            {currentDiscount > 0 && (
              <button type="button" className="vt-adj-remove-btn" onClick={handleRemove}>
                <FaTrash size={11} /> Remove Discount
              </button>
            )}
            <div className="vt-adj-btn-group">
              <button type="button" className="vt-adj-cancel-btn" onClick={onClose}>
                Cancel
              </button>
              <button type="submit" className="vt-adj-save-btn">
                <FaCheck size={12} /> Apply Discount
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

/**
 * RoundOffModal Component
 * Automatically detects decimals, calculates nearest round off, and allows 1-click apply or custom adjustment.
 */
export const RoundOffModal = ({
  isOpen,
  onClose,
  onApply,
  currentRoundOff = 0,
  netBeforeRoundOff = 0
}) => {
  const nearestWhole = Math.round(netBeforeRoundOff);
  const nearestDiff = Number((nearestWhole - netBeforeRoundOff).toFixed(2));
  const hasDecimals = Math.abs(nearestDiff) > 0.0001;

  const [customVal, setCustomVal] = useState(currentRoundOff || 0);

  useEffect(() => {
    if (isOpen) {
      setCustomVal(currentRoundOff !== 0 ? currentRoundOff : (hasDecimals ? nearestDiff : 0));
    }
  }, [isOpen, currentRoundOff, hasDecimals, nearestDiff]);

  if (!isOpen) return null;

  const handleApplyNearest = () => {
    onApply(nearestDiff);
    onClose();
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const val = Number(customVal) || 0;
    onApply(val);
    onClose();
  };

  const handleRemove = () => {
    onApply(0);
    onClose();
  };

  const previewGrandTotal = Number((netBeforeRoundOff + (Number(customVal) || 0)).toFixed(2));

  return (
    <div className="vt-adj-backdrop" onClick={onClose}>
      <div className="vt-adj-card" onClick={(e) => e.stopPropagation()}>
        <div className="vt-adj-header">
          <div className="vt-adj-title-box">
            <div className="vt-adj-icon roundoff">
              <FaCoins size={15} />
            </div>
            <div>
              <h3 className="vt-adj-title">{currentRoundOff !== 0 ? 'Change Round Off' : 'Add Round Off'}</h3>
              <p className="vt-adj-sub">Adjust Grand Total to the nearest integer rupee.</p>
            </div>
          </div>
          <button type="button" className="vt-adj-close" onClick={onClose}>
            <FaTimes size={13} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="vt-adj-body">
          <div className="vt-adj-info-row">
            <span>Amount Before Round Off:</span>
            <strong>₹ {netBeforeRoundOff.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
          </div>

          {/* Prominent Nearest Round Off Action */}
          {hasDecimals && (
            <div className="vt-adj-nearest-box">
              <div className="nearest-top">
                <FaCalculator className="calc-ic" />
                <div>
                  <div className="nearest-title">Nearest Integer Round Off</div>
                  <div className="nearest-desc">
                    Rounds <strong>₹ {netBeforeRoundOff.toFixed(2)}</strong> to <strong>₹ {nearestWhole.toFixed(2)}</strong>
                  </div>
                </div>
              </div>
              <div className="nearest-figure-row">
                <span className="figure-lbl">Round Off Figure:</span>
                <span className={`figure-badge ${nearestDiff >= 0 ? 'positive' : 'negative'}`}>
                  {nearestDiff >= 0 ? `+ ₹${nearestDiff.toFixed(2)}` : `- ₹${Math.abs(nearestDiff).toFixed(2)}`}
                </span>
              </div>
              <button
                type="button"
                className="vt-adj-apply-nearest-btn"
                onClick={handleApplyNearest}
              >
                <FaCheck /> Apply Nearest Round Off ({nearestDiff >= 0 ? `+₹${nearestDiff.toFixed(2)}` : `-₹${Math.abs(nearestDiff).toFixed(2)}`})
              </button>
            </div>
          )}

          {/* Custom Round Off Input */}
          <div className="vt-adj-field" style={{ marginTop: hasDecimals ? '8px' : '0' }}>
            <label>Custom Round Off Figure (₹):</label>
            <input
              type="number"
              step="0.01"
              className="vt-adj-input"
              value={customVal}
              onChange={(e) => setCustomVal(e.target.value)}
              placeholder="e.g. -0.40 or +0.60"
            />
          </div>

          {/* Preview Final Grand Total */}
          <div className="vt-adj-preview-banner">
            <div className="preview-line">
              <span>Applied Round Off:</span>
              <strong>{(Number(customVal) || 0) >= 0 ? `+ ₹${Number(customVal || 0).toFixed(2)}` : `- ₹${Math.abs(Number(customVal || 0)).toFixed(2)}`}</strong>
            </div>
            <div className="preview-line final">
              <span>Final Grand Total:</span>
              <strong>₹ {previewGrandTotal.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
            </div>
          </div>

          <div className="vt-adj-footer">
            {currentRoundOff !== 0 && (
              <button type="button" className="vt-adj-remove-btn" onClick={handleRemove}>
                <FaTrash size={11} /> Remove Round Off
              </button>
            )}
            <div className="vt-adj-btn-group">
              <button type="button" className="vt-adj-cancel-btn" onClick={onClose}>
                Cancel
              </button>
              <button type="submit" className="vt-adj-save-btn">
                <FaCheck size={12} /> Apply Round Off
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};
